import React from 'react';
import { Box, Tooltip } from '@mui/material';
import StatusChip from '../../../common/StatusChip';
import type { ProcessStatusColor } from '../../../../services/types/processStatus.types';

interface ProcessStatusCellProps {
  /** Common wording from the API (In Progress, Complete, Not Started, ...). */
  statusText?: string | null;
  statusColor?: ProcessStatusColor;
  /** User-defined stage name the status is about; shown on hover only, so a long name never breaks the grid. */
  stageName?: string | null;
  onStatusClick: () => void;
}

const colorMap: Record<string, string> = {
  success: '#528900',
  warning: '#E7B317',
  info: '#5656FF',
  default: '#9C9C9C',
  rejected: '#E9004A', // Special color for rejected status
};

const backgroundColorMap: Record<string, string> = {
  success: '#5289001A',
  warning: '#FBB32C1A',
  info: '#5656FF1A',
  default: '#9C9C9C1A',
  rejected: '#E9004A1A', // Special background for rejected status
};

const ProcessStatusCell: React.FC<ProcessStatusCellProps> = ({
  statusText,
  statusColor,
  stageName,
  onStatusClick,
}) => {
  const cellWrapperSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  };

  if (!statusText) {
    return <Box sx={cellWrapperSx} />;
  }

  // Use 'rejected' color for "Rejected" status text, otherwise use backend's color
  const isRejected = statusText === 'Rejected';
  const colorKey = isRejected ? 'rejected' : (statusColor || 'default');

  const chip = (
    <StatusChip
      label={statusText}
      sx={{
        color: colorMap[colorKey],
        backgroundColor: backgroundColorMap[colorKey],
        borderRadius: '16px',
        height: '20px',
        fontSize: '12px',
        fontWeight: 500,
      }}
    />
  );

  // Hover shows the stage the status is about; the click opens the summary.
  const tooltipTitle = stageName
    ? `${stageName} - ${statusText}`
    : 'Click to view process summary';

  return (
    <Box sx={cellWrapperSx}>
      <Tooltip title={tooltipTitle} arrow>
        <Box
          component="span"
          onClick={onStatusClick}
          sx={{ cursor: 'pointer', display: 'inline-flex' }}
        >
          {chip}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ProcessStatusCell;
